import PropTypes from 'prop-types';
import React, { useCallback, useMemo, useState } from 'react';
import { Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import VerifyClient from './verify/client/VerifyClient';
import VerifyFamily from './verify/family/VerifyFamily';
import VerifyMyself from './verify/myself/VerifyMyself';
import useUpdateEffect from 'react/shared/hooks/UseUpdateEffect';
import { matchingAddresses } from 'react/shared/utils/Address';
import api from 'react/signups/card/api.js';
import { USER_TYPES } from 'react/signups/card/recipes/constants';
import { goToNextStep, overrideNextModule } from 'react/signups/card/routing/navigator';
import cardAccountSignupShape from 'react/signups/card/shapes/CardAccountSignup';
import cardAccountTrustShape from 'react/signups/card/shapes/CardAccountTrust';
import organizationShape from 'react/signups/card/shapes/Organization';
import recipeShape from 'react/signups/card/shapes/Recipe';
import userShape from 'react/signups/card/shapes/User';
import { convertDate } from 'react/signups/card/utils';

export default function Idv({
  account,
  cardAccountSignup,
  cardAccountTrust,
  organization,
  recipe,
  setUser,
  user,
}) {
  const location = useLocation();
  const navigate = useNavigate();
  const userType = cardAccountSignup.userType;

  // State hooks ------------------------------------------------

  const shouldVerifyIdentity = useMemo(() => {
    if (userType === USER_TYPES.CLIENT) {
      return !(
        cardAccountTrust.trusteeCategory === 'Bank or credit union' ||
        cardAccountTrust.trusteeCategory === 'Trust company'
      );
    }
    return true;
  }, [cardAccountTrust, userType]);

  const sameAdminAndCardholderAddress = useMemo(() => {
    if (!account.cardholder?.address) return true;

    return matchingAddresses(organization.address, account.cardholder?.address);
  }, [account, organization]);

  const [moduleComplete, setModuleComplete] = useState(false);

  const [verificationData, setVerificationData] = useState(
    shouldVerifyIdentity
      ? {
          ssn: '',
          ssnConfirmation: '',
          dateOfBirth: convertDate(user.dob) || '',
        }
      : {
          dateOfBirth: convertDate(user.dob) || '',
        },
  );

  // Effect hooks and memoized callbacks ------------------------

  const buildRecipe = useCallback(() => {
    let nextModule = recipe.variants[userType].next;

    if (userType === USER_TYPES.FAMILY) {
      if (sameAdminAndCardholderAddress) {
        nextModule = recipe.variants[userType].alternativeNext.sameAddress;
      } else {
        nextModule = recipe.variants[userType].alternativeNext.differentAddress;
      }
    } else if (userType === USER_TYPES.CLIENT) {
      nextModule = recipe.variants[userType].alternativeNext.sntFlow;
    }

    return overrideNextModule({
      nextModule,
      recipe,
      userType,
    });
  }, [sameAdminAndCardholderAddress, recipe, userType]);

  const localRecipe = useMemo(() => buildRecipe(), [buildRecipe]);

  // Move to next step or mark module complete
  const onNavigate = useCallback(() => {
    goToNextStep({
      cardAccountSignupId: cardAccountSignup.id,
      location,
      navigate,
      moduleComplete,
      setModuleComplete,
      recipe: localRecipe,
      userType,
    });
  }, [
    location,
    navigate,
    localRecipe,
    userType,
    cardAccountSignup,
    moduleComplete,
    setModuleComplete,
  ]);

  // Send module payload and update Application-level state
  useUpdateEffect(() => {
    if (!moduleComplete) return;

    const params = {
      user: shouldVerifyIdentity
        ? {
            dob: verificationData.dateOfBirth,
            ssn: verificationData.ssn,
            ssnConfirmation: verificationData.ssnConfirmation,
          }
        : {
            dob: verificationData.dateOfBirth,
          },
    };

    api
      .identitySignup(user.id, params)
      .then((response) => {
        const userResponse = response.data.user.data;
        const { attributes, id } = userResponse;
        setUser({ id, ...attributes }); // set Application-level state
      })
      .catch(() => {
        Truelink.flash('error', 'There was an error verifying your identity');
        setModuleComplete(false);
      });
  }, [moduleComplete]);

  // navigate to next step when Module-level state changes in response to form submit
  useUpdateEffect(() => {
    onNavigate();
  }, [verificationData]);

  // Navigate to next module when Application-level state changes in response to api success
  useUpdateEffect(() => {
    onNavigate();
  }, [user]);

  // Pass-down state, props and handlers ------------------------------

  const passDownValues = {
    firstName: user.firstName || '',
    verificationData,
    moduleComplete,
  };

  const passDownHandlers = {
    setVerificationData,
  };

  const verify = (
    <>
      {userType === USER_TYPES.CLIENT && (
        <VerifyClient
          {...passDownValues}
          {...passDownHandlers}
          shouldVerifyIdentity={shouldVerifyIdentity}
        />
      )}
      {userType === USER_TYPES.FAMILY && <VerifyFamily {...passDownValues} {...passDownHandlers} />}
      {userType === USER_TYPES.MYSELF && <VerifyMyself {...passDownValues} {...passDownHandlers} />}
    </>
  );

  return (
    <Routes>
      <Route element={verify} path="/verify" />
      <Route element={<Navigate replace to={recipe.defaultPath} />} path="/*" />
    </Routes>
  );
}

Idv.propTypes = {
  account: PropTypes.object,
  cardAccountSignup: cardAccountSignupShape,
  cardAccountTrust: cardAccountTrustShape,
  organization: organizationShape,
  recipe: recipeShape.isRequired,
  setUser: PropTypes.func.isRequired,
  user: userShape.isRequired,
};
